import { Box, Flex, Select, Spinner, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { MultiSelect } from 'react-multi-select-component';

import {
  useAccessControlItems,
  useAccessControlUsers,
} from '../../../hooks/useAccessControl';
import {
  AccessLinks,
  accessControlError,
  accessControlService,
} from '../../../services/accessControlService';
import { filterOptions, userLabel } from '../../../utils/accessControl';
import { Internationalization } from '../../../utils/InternationalizationMultSelect';
import { apllyToast } from '../../Global/Toast2.0';

interface Props {
  value: AccessLinks;
  onChange: (value: AccessLinks) => void;
}

// Funções e permissões de um usuário novo, com atalho para copiar de alguém
// que já tem o mesmo papel.
export function AccessLinksField({ value, onChange }: Props) {
  const roles = useAccessControlItems('role');
  const permissions = useAccessControlItems('permission');
  const users = useAccessControlUsers();
  const [copying, setCopying] = useState(false);
  const [copyFrom, setCopyFrom] = useState('');

  const roleOptions = (roles.data || []).map(item => ({
    value: item.id,
    label: item.name,
  }));
  const permissionOptions = (permissions.data || []).map(item => ({
    value: item.id,
    label: item.name,
  }));

  const handleCopy = async (sourceId: string) => {
    setCopyFrom(sourceId);

    if (!sourceId) {
      return;
    }

    setCopying(true);
    try {
      const links = await accessControlService.getAccessLinks(sourceId);

      onChange({
        roles_ids: Array.from(
          new Set([...value.roles_ids, ...links.roles_ids]),
        ),
        permissions_ids: Array.from(
          new Set([...value.permissions_ids, ...links.permissions_ids]),
        ),
      });
      apllyToast('success', 'Funções e permissões copiadas.');
    } catch (err) {
      apllyToast(
        'error',
        accessControlError(err, 'Não foi possível copiar os acessos.'),
      );
    } finally {
      setCopying(false);
      setCopyFrom('');
    }
  };

  const handleRoles = (selected: { value: string }[]) => {
    onChange({ ...value, roles_ids: selected.map(item => item.value) });
  };

  const handlePermissions = (selected: { value: string }[]) => {
    onChange({ ...value, permissions_ids: selected.map(item => item.value) });
  };

  const loading = roles.isLoading || permissions.isLoading;

  return (
    <Box mt="1rem">
      <Text fontWeight="bold">Funções e permissões</Text>
      <Text fontSize="xs" color="gray.500" mb="2">
        Opcional. Também dá para atribuir depois, editando o usuário.
      </Text>

      {loading ? (
        <Spinner size="sm" />
      ) : (
        <>
          <p>Copiar acessos de outro usuário</p>
          <Flex align="center" gridGap="2" mb="2">
            <Select
              value={copyFrom}
              onChange={event => handleCopy(event.target.value)}
              placeholder="Escolha um usuário com o mesmo papel"
              isDisabled={copying}
            >
              {(users.data || [])
                .filter(user => user.status)
                .map(user => (
                  <option key={user.id} value={user.id}>
                    {userLabel(user, true)}
                  </option>
                ))}
            </Select>
            {copying && <Spinner size="sm" />}
          </Flex>

          <p>Funções</p>
          <MultiSelect
            options={roleOptions}
            value={roleOptions.filter(option =>
              value.roles_ids.includes(option.value),
            )}
            onChange={handleRoles}
            labelledBy="Funções"
            hasSelectAll={false}
            filterOptions={filterOptions}
            overrideStrings={Internationalization}
          />

          <Text mt="2">Permissões</Text>
          <MultiSelect
            options={permissionOptions}
            value={permissionOptions.filter(option =>
              value.permissions_ids.includes(option.value),
            )}
            onChange={handlePermissions}
            labelledBy="Permissões"
            hasSelectAll={false}
            filterOptions={filterOptions}
            overrideStrings={Internationalization}
          />
        </>
      )}
    </Box>
  );
}
